import { useState } from "react";
import type { ProjectListItem } from "@/mocks/admin/projects";

type Tab = {
  key: string;
  label: string;
  count: number;
};

type Props = {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (key: string) => void;
  search: string;
  onSearchChange: (v: string) => void;
  exportRows: ProjectListItem[];
};

function csvEscape(value: string | number | boolean): string {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsv(rows: ProjectListItem[]): string {
  const headers = [
    "Project ID",
    "Customer",
    "Move Type",
    "Date",
    "Time",
    "From",
    "To",
    "Truck",
    "Crew Count",
    "Lead Mover",
    "Status",
    "Total (USD)",
    "Deposit Paid",
  ];
  const lines = [headers.map(csvEscape).join(",")];
  rows.forEach((p) => {
    lines.push(
      [
        p.id,
        p.customer,
        p.moveType,
        p.date,
        p.time,
        `${p.fromCity}`,
        `${p.toCity}`,
        p.truck,
        p.crewCount,
        p.leadCrew,
        p.status,
        p.total,
        p.depositPaid ? "Yes" : "No",
      ]
        .map(csvEscape)
        .join(","),
    );
  });
  return lines.join("\n");
}

export default function ProjectsToolbar({
  tabs,
  activeTab,
  onTabChange,
  search,
  onSearchChange,
  exportRows,
}: Props) {
  const [view, setView] = useState<"list" | "grid">("list");
  const [filterOpen, setFilterOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportToast, setExportToast] = useState("");

  const handleExport = () => {
    if (exportRows.length === 0) {
      setExportToast("Nothing to export — try clearing your filters");
      setTimeout(() => setExportToast(""), 3500);
      setExportOpen(false);
      return;
    }
    const csv = buildCsv(exportRows);
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const today = new Date();
    const stamp = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate(),
    ).padStart(2, "0")}`;
    const tabKey = activeTab === "all" ? "all-projects" : activeTab;
    const filename = `haulmark-${tabKey}-${stamp}.csv`;
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setExportToast(
      `Exported ${exportRows.length} project${exportRows.length === 1 ? "" : "s"} as ${filename}`,
    );
    setTimeout(() => setExportToast(""), 4000);
    setExportOpen(false);
  };

  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      {/* Tabs */}
      <div className="flex items-center gap-1 px-3 pt-2 border-b border-ink-100 overflow-x-auto scrollbar-thin">
        {tabs.map((t) => {
          const active = t.key === activeTab;
          return (
            <button
              key={t.key}
              onClick={() => onTabChange(t.key)}
              className={`relative px-3 py-3 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                active ? "text-ink-900" : "text-ink-500 hover:text-ink-700"
              }`}
            >
              <span className="flex items-center gap-2">
                {t.label}
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    active ? "bg-ink-900 text-white" : "bg-cream-100 text-ink-600"
                  }`}
                >
                  {t.count}
                </span>
              </span>
              {active && (
                <span className="absolute left-3 right-3 -bottom-px h-0.5 bg-brand-500 rounded-full"></span>
              )}
            </button>
          );
        })}
      </div>

      {/* Toolbar row */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3">
        <div className="relative flex-1 min-w-0">
          <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by project ID, customer, address…"
            className="w-full text-sm pl-10 pr-3 py-2.5 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <button
              onClick={() => setFilterOpen((p) => !p)}
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line"></i>
              Filters
              <span className="text-[10px] font-bold bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded">
                2
              </span>
            </button>
            {filterOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-ink-100 rounded-lg p-4 z-30">
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">
                  Date range
                </div>
                <div className="flex gap-2 mb-4">
                  <input
                    type="date"
                    className="flex-1 text-xs px-2 py-1.5 rounded-md border border-ink-200 outline-none"
                  />
                  <input
                    type="date"
                    className="flex-1 text-xs px-2 py-1.5 rounded-md border border-ink-200 outline-none"
                  />
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">
                  Truck size
                </div>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {["16ft Van", "20ft Box", "26ft Box", "26ft + Trailer"].map((t) => (
                    <button
                      key={t}
                      className="text-xs px-2.5 py-1 rounded-full border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer"
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-ink-100">
                  <button className="text-xs font-semibold text-ink-500 hover:text-ink-900 cursor-pointer">
                    Reset
                  </button>
                  <button
                    onClick={() => setFilterOpen(false)}
                    className="text-xs font-semibold bg-ink-900 text-white px-3 py-1.5 rounded-md hover:bg-ink-800 cursor-pointer"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 bg-cream-100 p-1 rounded-md">
            <button
              onClick={() => setView("list")}
              className={`w-8 h-8 flex items-center justify-center rounded cursor-pointer ${
                view === "list" ? "bg-white text-ink-900" : "text-ink-500 hover:text-ink-900"
              }`}
            >
              <i className="ri-list-check-2"></i>
            </button>
            <button
              onClick={() => setView("grid")}
              className={`w-8 h-8 flex items-center justify-center rounded cursor-pointer ${
                view === "grid" ? "bg-white text-ink-900" : "text-ink-500 hover:text-ink-900"
              }`}
            >
              <i className="ri-grid-line"></i>
            </button>
          </div>

          <div className="relative">
            <button
              onClick={() => setExportOpen((p) => !p)}
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line"></i>
              Export
              <i className="ri-arrow-down-s-line text-ink-400"></i>
            </button>
            {exportOpen && (
              <>
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setExportOpen(false)}
                ></div>
                <div className="absolute right-0 mt-2 w-72 bg-white border border-ink-100 rounded-lg p-2 z-30">
                  <div className="px-3 py-2 border-b border-ink-100">
                    <div className="text-xs font-bold uppercase tracking-wider text-ink-500">
                      Export {exportRows.length} {exportRows.length === 1 ? "project" : "projects"}
                    </div>
                    <div className="text-[11px] text-ink-500 mt-0.5">
                      Includes everything from current filters
                    </div>
                  </div>
                  <button
                    onClick={handleExport}
                    className="w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-md hover:bg-cream-100 cursor-pointer mt-1"
                  >
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-emerald-100 text-emerald-700 shrink-0">
                      <i className="ri-file-excel-2-line text-base"></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-ink-900">
                        Download CSV
                      </span>
                      <span className="block text-[11px] text-ink-500 mt-0.5">
                        Open in Excel, Numbers or Google Sheets
                      </span>
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      window.print();
                      setExportOpen(false);
                    }}
                    className="w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-md hover:bg-cream-100 cursor-pointer"
                  >
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-ink-100 text-ink-700 shrink-0">
                      <i className="ri-printer-line text-base"></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-ink-900">
                        Print / Save as PDF
                      </span>
                      <span className="block text-[11px] text-ink-500 mt-0.5">
                        Uses your browser print dialog
                      </span>
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Export toast */}
      <div
        className={`fixed bottom-6 right-6 z-[70] transition-all duration-300 ${
          exportToast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md border border-ink-800 flex items-center gap-2 max-w-md">
          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
            <i className="ri-download-line text-sm"></i>
          </span>
          {exportToast}
        </div>
      </div>
    </div>
  );
}